import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAllShippingTiers, useShippingSettings, type ShippingTier } from "@/hooks/useShipping";
import { useQueryClient } from "@tanstack/react-query";
import { Save, Plus, Trash2, Truck } from "lucide-react";

const AdminShipping = () => {
  const { data: dbTiers = [], isLoading: tiersLoading } = useAllShippingTiers();
  const { data: dbSettings, isLoading: settingsLoading } = useShippingSettings();
  const queryClient = useQueryClient();

  const [tiers, setTiers] = useState<ShippingTier[]>([]);
  const [threshold, setThreshold] = useState("50");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (dbTiers.length > 0) setTiers(dbTiers);
  }, [dbTiers]);

  useEffect(() => {
    if (dbSettings) setThreshold(String(dbSettings.free_shipping_threshold));
  }, [dbSettings]);

  const updateTier = (index: number, field: keyof ShippingTier, value: any) => {
    setTiers((prev) => prev.map((t, i) => (i === index ? { ...t, [field]: value } : t)));
  };

  const addTier = () => {
    setTiers((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        label: "New Tier",
        min_weight_kg: 0,
        max_weight_kg: null,
        price: 0,
        position: prev.length + 1,
        active: true,
      },
    ]);
  };

  const removeTier = (index: number) => {
    setTiers((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Delete existing tiers and re-insert
      await (supabase.from("shipping_tiers" as any) as any).delete().neq("id", "00000000-0000-0000-0000-000000000000");

      if (tiers.length > 0) {
        const rows = tiers.map((t, i) => ({
          label: t.label,
          min_weight_kg: Number(t.min_weight_kg),
          max_weight_kg: t.max_weight_kg !== null && t.max_weight_kg !== undefined ? Number(t.max_weight_kg) : null,
          price: Number(t.price),
          position: i + 1,
          active: t.active,
        }));
        const { error } = await (supabase.from("shipping_tiers" as any) as any).insert(rows);
        if (error) throw error;
      }

      // Update threshold
      if (dbSettings?.id) {
        const { error } = await (supabase.from("shipping_settings" as any) as any)
          .update({ free_shipping_threshold: Number(threshold) })
          .eq("id", dbSettings.id);
        if (error) throw error;
      }

      queryClient.invalidateQueries({ queryKey: ["shipping-tiers"] });
      queryClient.invalidateQueries({ queryKey: ["shipping-tiers-all"] });
      queryClient.invalidateQueries({ queryKey: ["shipping-settings"] });
      toast.success("Shipping settings saved");
    } catch (err: any) {
      console.error(err);
      toast.error("Failed to save shipping settings");
    } finally {
      setSaving(false);
    }
  };

  if (tiersLoading || settingsLoading) {
    return <div className="animate-pulse text-muted-foreground p-8">Loading…</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl font-bold flex items-center gap-2">
          <Truck className="w-6 h-6" /> Shipping
        </h1>
        <Button onClick={handleSave} disabled={saving}>
          <Save className="w-4 h-4 mr-1.5" />
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Free shipping threshold */}
        <div className="bg-card rounded-lg border border-border p-5 space-y-4">
          <h2 className="font-display font-semibold">Free Shipping</h2>
          <div className="space-y-2">
            <Label className="font-body">Free shipping threshold (£)</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              className="font-body"
            />
            <p className="text-xs text-muted-foreground font-body">
              Orders above this amount get free shipping. Set to 0 to disable.
            </p>
          </div>
        </div>

        {/* Weight-based tiers */}
        <div className="lg:col-span-2 bg-card rounded-lg border border-border p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-display font-semibold">Weight-Based Tiers</h2>
            <Button variant="outline" size="sm" onClick={addTier}>
              <Plus className="w-4 h-4 mr-1" /> Add Tier
            </Button>
          </div>

          {tiers.length === 0 ? (
            <p className="text-sm text-muted-foreground font-body py-4">
              No shipping tiers configured. A flat rate of £4.99 will be used.
            </p>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-[1fr_0.7fr_0.7fr_0.7fr_auto_auto] gap-2 text-xs font-body font-medium text-muted-foreground px-1">
                <span>Label</span>
                <span>Min (kg)</span>
                <span>Max (kg)</span>
                <span>Price (£)</span>
                <span>Active</span>
                <span></span>
              </div>
              {tiers.map((tier, index) => (
                <div
                  key={tier.id}
                  className="grid grid-cols-[1fr_0.7fr_0.7fr_0.7fr_auto_auto] gap-2 items-center"
                >
                  <Input
                    value={tier.label}
                    onChange={(e) => updateTier(index, "label", e.target.value)}
                    className="font-body text-sm"
                  />
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={tier.min_weight_kg}
                    onChange={(e) => updateTier(index, "min_weight_kg", parseFloat(e.target.value) || 0)}
                    className="font-body text-sm"
                  />
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={tier.max_weight_kg ?? ""}
                    placeholder="∞"
                    onChange={(e) =>
                      updateTier(index, "max_weight_kg", e.target.value ? parseFloat(e.target.value) : null)
                    }
                    className="font-body text-sm"
                  />
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={tier.price}
                    onChange={(e) => updateTier(index, "price", parseFloat(e.target.value) || 0)}
                    className="font-body text-sm"
                  />
                  <input
                    type="checkbox"
                    checked={tier.active}
                    onChange={(e) => updateTier(index, "active", e.target.checked)}
                    className="rounded mx-auto"
                  />
                  <button
                    onClick={() => removeTier(index)}
                    className="p-1.5 text-destructive hover:bg-destructive/10 rounded transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <p className="text-xs text-muted-foreground font-body pt-2 border-t border-border">
            Leave "Max (kg)" empty for the highest tier to capture all heavier orders.
            Products without weight data are treated as 0kg.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminShipping;
